import React, { useState } from 'react';
import { SlidersHorizontal, X, Info } from 'lucide-react';
import ContactBuyerPreferencesEditor from '../../contacts/components/ContactBuyerPreferencesEditor';

/**
 * Set one set of buying preferences across every selected Database row.
 *
 * Christian Gravias, Monday DOMA 12840979019 (2026-08-19): "another agent has 200
 * buyers looking to purchase a 1 bedroom apartment in Melbourne for a price of
 * $100k-$300k. How can we add them all in and specify those buying preferences?"
 * The per-row Buying Preferences button already existed; this is the same form
 * applied to a selection.
 *
 * Deliberately the SAME editor as the single-row modal, so there is one form to
 * learn and one payload shape to maintain. Two things are extra here: the choice
 * between widening and replacing, and whether a joint second buyer on the row is
 * included.
 */
const MODES = [
  {
    id: 'merge',
    label: 'Add to what they already have',
    hint: 'Ranges widen to cover both, suburbs and types are added to their list. '
      + 'Nothing a buyer already told us is lost. Safest for a big selection.',
  },
  {
    id: 'replace',
    label: 'Replace their current preferences',
    hint: 'Every field you set below overwrites what is there now. Use when the '
      + 'existing values are noise rather than history.',
  },
];

const BulkBuyerPreferencesModal = ({ count, view, onClose, onApply }) => {
  const [mode, setMode] = useState('merge');
  const [includeSecond, setIncludeSecond] = useState(false);

  const label = `Applying to ${count} selected ${count === 1 ? 'contact' : 'contacts'}`;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="w-4 h-4 text-[#3a99a3] shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              Set buying preferences · {count} selected
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Mode + scope live above the form: they change what saving DOES, so the
              agent has to see them before filling anything in. */}
          <div className="px-4 sm:px-8 pt-5 space-y-3">
            <div className="grid gap-2">
              {MODES.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    mode === m.id
                      ? 'border-[#55C5D0] bg-[#F0FDFE]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="bulk-pref-mode"
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                    className="mt-0.5 w-4 h-4 text-[#55C5D0] focus:ring-[#55C5D0]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-bold text-gray-900">{m.label}</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">{m.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSecond}
                onChange={(e) => setIncludeSecond(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
              />
              <span className="text-[11px] leading-snug text-gray-500">
                Also apply to the second person on each row (a couple buying
                together is one row and two contacts, and each keeps their own
                preferences).
              </span>
            </label>

            {view === 'owners' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                These are owner rows. Buying preferences still apply. An owner you
                are calling is often also a buyer, but they will not change the
                Buyers grid columns.
              </p>
            )}
          </div>

          <ContactBuyerPreferencesEditor
            onBulkSave={(payload) => onApply({ preferences: payload, mode, includeSecond })}
            bulkLabel={label}
            saveLabel={`Apply to ${count} ${count === 1 ? 'Contact' : 'Contacts'}`}
          />
        </div>
      </div>
    </div>
  );
};

export default BulkBuyerPreferencesModal;
